import { useEffect, useRef, useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Volume2, Pause, Loader2, Search, Pin, PinOff, CheckCircle2, Circle, ChevronLeft, ChevronRight } from 'lucide-react';

const LANG_LABEL = { ta: 'Tamil', hi: 'Hindi', ne: 'Nepali', kn: 'Kannada' };
const STATUS_OPTIONS = [
  { k: 'all', label: 'All' },
  { k: 'missing_audio', label: 'Missing audio' },
  { k: 'with_audio', label: 'Has audio' },
];
const LANG_OPTIONS = [
  { k: '', label: 'All langs' },
  { k: 'ta', label: 'Tamil' },
  { k: 'hi', label: 'Hindi' },
  { k: 'ne', label: 'Nepali' },
  { k: 'kn', label: 'Kannada' },
];

/**
 * CEO-only admin list of every translation with audio status + priority control.
 * Renders below the AudioPrewarmPanel. Supports:
 *   - filter by status (all / missing / has) and language
 *   - search SOP title + outlet
 *   - play audio inline (one active player)
 *   - pin SOPs as priority (goes to front of worker queue)
 *   - paginated (200 per page)
 */
export default function AudioTranslationsList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(200);
  const [status, setStatus] = useState('missing_audio');
  const [lang, setLang] = useState('');
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState('');
  const [playerState, setPlayerState] = useState('idle'); // idle | loading | playing | error
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = new Audio();

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/sop-translations/audio/list', {
        params: { status, lang: lang || undefined, q: qDebounced || undefined, offset, limit },
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error('Failed to load translations');
    } finally {
      setLoading(false);
    }
  }, [status, lang, qDebounced, offset, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOffset(0); }, [status, lang, qDebounced]);

  useEffect(() => {
    const a = audioRef.current;
    const onEnded = () => setPlayerState('idle');
    const onError = () => setPlayerState('error');
    a.addEventListener('ended', onEnded);
    a.addEventListener('error', onError);
    return () => {
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('error', onError);
      try { a.pause(); } catch (_) { /* no-op */ }
    };
  }, []);

  const play = async (item) => {
    const a = audioRef.current;
    if (activeId === item.translation_id && playerState === 'playing') {
      a.pause();
      setPlayerState('idle');
      return;
    }
    try { a.pause(); } catch (_) { /* no-op */ }
    a.src = '';
    setActiveId(item.translation_id);
    setPlayerState('loading');
    try {
      const baseURL = api.defaults.baseURL;
      const token = localStorage.getItem('access_token') || '';
      const res = await fetch(`${baseURL}/sop-translations/${item.translation_id}/audio`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      a.src = URL.createObjectURL(blob);
      await a.play();
      setPlayerState('playing');
    } catch (e) {
      setPlayerState('error');
      toast.error('Playback failed');
    }
  };

  const togglePriority = async (sopId, newVal) => {
    try {
      await api.post('/sop-translations/audio/priority', { sop_ids: [sopId], priority: newVal });
      toast.success(newVal ? 'Pinned — will generate first' : 'Unpinned');
      // Optimistic update
      setItems((prev) => prev.map((x) => x.sop_id === sopId ? { ...x, priority: newVal } : x));
    } catch (_) {
      toast.error('Update failed');
    }
  };

  const bulkPriorityForVisible = async (newVal) => {
    const unique_sop_ids = [...new Set(items.map((i) => i.sop_id))];
    if (!unique_sop_ids.length) return;
    try {
      await api.post('/sop-translations/audio/priority', { sop_ids: unique_sop_ids, priority: newVal });
      toast.success(`${newVal ? 'Pinned' : 'Unpinned'} ${unique_sop_ids.length} SOP${unique_sop_ids.length === 1 ? '' : 's'}`);
      load();
    } catch (_) {
      toast.error('Bulk update failed');
    }
  };

  const hasMore = offset + limit < total;
  const pageStart = total ? offset + 1 : 0;
  const pageEnd = Math.min(offset + limit, total);

  return (
    <div className="mt-4 rounded-xl border bg-white p-4" style={{ borderColor: '#E5E1DC' }} data-testid="audio-translations-list">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold" style={{ color: '#1B2A4A' }}>Translation Library</h3>
          <p className="text-[11px]" style={{ color: '#6B7280' }}>
            Browse every translation · preview audio · pin priority SOPs to jump the queue
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => bulkPriorityForVisible(true)} className="text-[11px] px-2 py-1 rounded border inline-flex items-center gap-1"
            style={{ borderColor: '#F5C86B', color: '#8C560F', backgroundColor: '#FFFBEB' }}
            title="Pin every SOP currently visible — they'll generate first on next pre-warm"
            data-testid="audio-list-bulk-pin"
          >
            <Pin size={11} /> Pin all visible
          </button>
          <button onClick={() => bulkPriorityForVisible(false)} className="text-[11px] px-2 py-1 rounded border inline-flex items-center gap-1"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
            data-testid="audio-list-bulk-unpin"
          >
            <PinOff size={11} /> Unpin all
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="inline-flex rounded-md overflow-hidden border" style={{ borderColor: '#E5E1DC' }}>
          {STATUS_OPTIONS.map((opt) => (
            <button key={opt.k} onClick={() => setStatus(opt.k)} className="text-[11px] px-2.5 py-1 transition"
              style={{
                backgroundColor: status === opt.k ? '#2A7B88' : '#FFF',
                color: status === opt.k ? '#FFF' : '#6B7280',
              }}
              data-testid={`audio-list-status-${opt.k}`}
            >{opt.label}</button>
          ))}
        </div>
        <select value={lang} onChange={(e) => setLang(e.target.value)}
          className="text-[11px] px-2 py-1 rounded border bg-white"
          style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
          data-testid="audio-list-lang-filter"
        >
          {LANG_OPTIONS.map((o) => <option key={o.k} value={o.k}>{o.label}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: '#9CA3AF' }} />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SOP title or outlet…"
            className="w-full text-[11px] px-7 py-1 rounded border bg-white"
            style={{ borderColor: '#E5E1DC' }}
            data-testid="audio-list-search"
          />
        </div>
        <span className="text-[10px]" style={{ color: '#6B7280' }}>
          {loading ? 'Loading…' : total ? `${pageStart}–${pageEnd} of ${total.toLocaleString()}` : '0 results'}
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded-md" style={{ borderColor: '#E5E1DC' }}>
        <table className="w-full text-[11px]">
          <thead>
            <tr style={{ backgroundColor: '#F9FAFB', color: '#6B7280' }}>
              <th className="text-left px-2 py-1.5 font-medium w-6"></th>
              <th className="text-left px-2 py-1.5 font-medium">SOP</th>
              <th className="text-left px-2 py-1.5 font-medium">Outlet</th>
              <th className="text-left px-2 py-1.5 font-medium w-16">Lang</th>
              <th className="text-left px-2 py-1.5 font-medium w-20">Audio</th>
              <th className="text-left px-2 py-1.5 font-medium w-24">Size / Play</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const isActive = activeId === it.translation_id;
              const isPlaying = isActive && playerState === 'playing';
              const isLoading = isActive && playerState === 'loading';
              return (
                <tr key={it.translation_id} className="border-t" style={{ borderColor: '#F3F0EB' }} data-testid={`audio-list-row-${it.translation_id}`}>
                  <td className="px-2 py-1.5 align-middle">
                    <button onClick={() => togglePriority(it.sop_id, !it.priority)}
                      title={it.priority ? 'Pinned as priority — click to unpin' : 'Pin as priority — generates first'}
                      data-testid={`audio-list-pin-${it.translation_id}`}
                    >
                      {it.priority
                        ? <Pin size={13} style={{ color: '#BA7517', fill: '#F5C86B' }} />
                        : <PinOff size={13} style={{ color: '#D1D5DB' }} />}
                    </button>
                  </td>
                  <td className="px-2 py-1.5 align-middle">
                    <div className="font-medium truncate max-w-[280px]" style={{ color: '#1B2A4A' }}>{it.sop_title}</div>
                    {it.category && <div className="text-[10px]" style={{ color: '#9CA3AF' }}>{it.category}</div>}
                  </td>
                  <td className="px-2 py-1.5 align-middle truncate max-w-[180px]" style={{ color: '#6B7280' }}>{it.outlet_name || '—'}</td>
                  <td className="px-2 py-1.5 align-middle" style={{ color: '#1B2A4A' }}>{LANG_LABEL[it.lang] || it.lang}</td>
                  <td className="px-2 py-1.5 align-middle">
                    {it.has_audio
                      ? <span className="inline-flex items-center gap-1" style={{ color: '#2D8B5E' }}><CheckCircle2 size={11} /> cached</span>
                      : <span className="inline-flex items-center gap-1" style={{ color: '#9CA3AF' }}><Circle size={11} /> pending</span>}
                  </td>
                  <td className="px-2 py-1.5 align-middle">
                    {it.has_audio ? (
                      <button onClick={() => play(it)} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border"
                        style={{
                          backgroundColor: isPlaying ? '#F5C86B' : '#ECFDF5',
                          color: isPlaying ? '#8C560F' : '#2D8B5E',
                          borderColor: isPlaying ? '#F5C86B' : '#86EFAC',
                        }}
                        data-testid={`audio-list-play-${it.translation_id}`}
                      >
                        {isLoading ? <Loader2 size={10} className="animate-spin" />
                          : isPlaying ? <Pause size={10} /> : <Volume2 size={10} />}
                        <span>{it.audio_size_kb} KB</span>
                      </button>
                    ) : <span style={{ color: '#9CA3AF' }}>—</span>}
                  </td>
                </tr>
              );
            })}
            {!loading && !items.length && (
              <tr><td colSpan={6} className="text-center py-6" style={{ color: '#9CA3AF' }}>No matching translations</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pager */}
      <div className="flex items-center justify-between mt-2">
        <button onClick={() => setOffset((o) => Math.max(0, o - limit))} disabled={offset === 0}
          className="text-[11px] px-2 py-1 rounded border inline-flex items-center gap-1 disabled:opacity-40"
          style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
          data-testid="audio-list-prev"
        ><ChevronLeft size={11} /> Prev</button>
        <span className="text-[10px]" style={{ color: '#9CA3AF' }}>Page {Math.floor(offset / limit) + 1} of {Math.max(1, Math.ceil(total / limit))}</span>
        <button onClick={() => setOffset((o) => o + limit)} disabled={!hasMore}
          className="text-[11px] px-2 py-1 rounded border inline-flex items-center gap-1 disabled:opacity-40"
          style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
          data-testid="audio-list-next"
        >Next <ChevronRight size={11} /></button>
      </div>
    </div>
  );
}
